import { createContext, useContext } from 'react';

/**
 * The character a session is actually using — not the learner's current
 * preference. They differ the moment somebody changes their mind, and the face
 * has to match the voice that is actually speaking.
 *
 * Structural rather than a Relay generated type: `startSession` and
 * `refreshAudioToken` produce this shape from two different generated files,
 * and a context typed against either one would refuse the other.
 */
export type SessionCharacter = {
  artKey: string;
  audience: string;
  displayName: string;
  accentHex: string;
  intensity: number;
};

/**
 * `%future added value` is Relay's own member, not a schema one: a client built
 * against today's schema can be served by a server that has since added a
 * status. Carried rather than narrowed away, so a status this build has never
 * heard of arrives here intact instead of being coerced into one it knows.
 */
export type SessionStatus = 'ACTIVE' | 'ENDED' | 'DEBRIEFING' | 'DEBRIEFED' | 'FAILED' | '%future added value';

/**
 * A session whose audio plane is connected, as either mutation hands it over.
 *
 * `startSession` and `refreshAudioToken` return the same `StartSessionPayload`
 * on purpose — see `mutation.rs`'s note on why a token has to be re-mintable —
 * and that is what lets a fresh start and a resume converge on one value here.
 * Exactly one place builds the audio machine, so the two paths cannot drift.
 */
export type SessionCredentials = {
  sessionId: string;
  audioWsUrl: string;
  audioToken: string;
  character: SessionCharacter | null;
  status: SessionStatus;
  topic: string | null;
  /** The book a story talk is about, shown where a topic shows (storybooks phase 2 §3). */
  storyTitle: string | null;
};

/**
 * Session identity, credentials, and the audio connection — everything that is
 * the same whether the learner just started talking or came back to a
 * conversation already running.
 *
 * Deliberately *not* the transcript. `TutorTextDelta` arrives per token, and a
 * context carrying it would re-render every consumer on every token; the lines,
 * the error marks and the streamed reply stay local to `SessionView`, which is
 * the only thing that reads them.
 */
export type SessionContextValue = {
  credentials: SessionCredentials | null;
  /**
   * The capture graph is up and the socket is up. The microphone is not part
   * of this: it opens per press, or while hands-free is on, so the greeting
   * plays with none open (media-channel design §4.3).
   */
  ready: boolean;
  /**
   * Set once, when either plane of the session dies; cleared only when a new
   * session connects. There is no reconnect in this phase: an honest dead
   * state the learner can act on is the promise spec §12 row 4 makes, and a
   * silent live-looking UI is not.
   */
  dead: string | null;
  /** Hands-free opened an utterance on its own, rather than a press. */
  autoCapturing: boolean;
  /** `getUserMedia` refused, with the reason. */
  micError: string | null;
  /** A press is waiting on the microphone to open (§4.4). */
  micOpening: boolean;
  /**
   * An adult press ended before its microphone opened: a first permission
   * prompt, or a tap too quick to record. Cleared by the next press (§4.4).
   */
  micAllowed: boolean;
  /** `endSession` has answered. */
  ended: boolean;
  /**
   * The session was ended by something other than this tab's End -- the
   * server after a kid's goodbye, or another tab -- and `ended` is waiting on
   * the tutor's audio to drain.
   */
  closing: boolean;
  /**
   * How many turns the audio socket has reported ending, on this connection.
   *
   * `TTS_DONE` is sent once on every path out of a turn — a reply, a failure,
   * a cancellation — so this counts turns the server has finished with,
   * whatever became of them.
   *
   * It is here, and not local to the screen, because the socket is opened
   * before the conversation route is navigated to: a greeting that fails in
   * milliseconds ends its turn while `SessionView` is still mounting. Counted
   * in the provider, that terminator is still on the value when the screen
   * arrives — which is the whole difference between this and the event bus,
   * where the same fact is broadcast to nobody (`events.rs`).
   */
  turnsEnded: number;
  /**
   * The tutor is silent and a whole reply is kept, so a tap can replay it
   * (hear-again design §4.3). `SessionView` adds the turn's own conditions
   * through `canHearAgain`.
   */
  replayable: boolean;
  /** A replay is playing. The face shows `speaking` for it. */
  replaying: boolean;
  /** Replay the last whole line. Does nothing unless one can play. */
  hearAgain: () => void;
  /**
   * Start a new session. Resolves to its id, or to why it could not start —
   * copy for the learner, from `whyNotStarted`, never the server's message.
   */
  start: (topic?: string) => Promise<StartResult>;
  /**
   * Attach to a session that is already running.
   *
   * Idempotent per id, so a caller may call it unconditionally from an effect
   * without tracking whether it has already fired.
   */
  resume: (sessionId: string) => void;
  end: () => void;
  /**
   * Declare the session dead, from a plane the provider does not watch itself.
   *
   * The provider sees the audio socket close; it does not see the GraphQL
   * subscription end, and the server ends that stream when the socket tears
   * down. Without a way in from there, the last thing a learner hears from a
   * dead session is silence under a button still offering to record.
   *
   * First reason wins, like the provider's own — a death has one cause and the
   * second report is an echo of the first.
   */
  die: (why: string) => void;
  /**
   * The subscription reported `SessionEnded`: an `endSession` from this tab or
   * another, or the server closing a kid's story talk after its goodbye
   * (storybooks phase 2 §2.6). `SessionView` calls it from its subscription,
   * which is the only one; `ended` follows once any goodbye has played.
   */
  serverEnded: () => void;
  beginUtterance: () => void;
  endUtterance: () => void;
  setHandsFree: (on: boolean) => void;
};

/**
 * `null` rather than an inert default, so a consumer mounted outside the
 * provider fails loudly in `useSession` instead of silently holding a session
 * that never connects.
 */
export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

export type StartResult = { sessionId: string } | { why: string };
